/**
 * Paket 2310e: "Alle Links sollen sich in einem neuen Tab öffnen lassen".
 *
 * Die App nutzt einen eigenen History-Router (lib/route.ts). Navigation per
 * onClick + navigateGlobal() erzeugt keine echten Links, daher funktionieren
 * Strg/Cmd-Klick, Mittelklick und Rechtsklick "In neuem Tab öffnen" nicht.
 *
 * Zwei Bausteine:
 *  - <AppLink to|page,sub,tab,extra>   echter <a href>. Normaler Klick bleibt
 *    im selben Tab (SPA-Navigation), Modifier-Klicks überlässt er dem Browser.
 *  - rowLinkProps(url, onOpen)         für Zeilen/Karten/Buttons, die kein <a>
 *    sein können (div/tr/button): normaler Klick ruft onOpen(), Strg/Cmd/Shift
 *    oder Mittelklick öffnen `url` per window.open in einem neuen Tab.
 *
 * target=_blank wird bewusst NICHT erzwungen.
 */
import React from 'react';
import { buildPath, navigateGlobal, parsePath, type PageId } from './route';

/** true bei Strg/Cmd/Shift-Klick oder Mittelklick (Auxiliary Button). */
export function isNewTabClick(e: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number }): boolean {
  return !!(e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1);
}

export function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Navigiert per SPA zu einem Pfad (z.B. "/pflege/42/aufgaben"). */
export function navigateToPath(path: string): void {
  const r = parsePath(path.split('?')[0].split('#')[0]);
  // Nur die vorhandenen Segmente durchreichen (gleiche Aufrufform wie bisher).
  if (r.extra) navigateGlobal(r.page, r.sub, r.tab, r.extra);
  else if (r.tab) navigateGlobal(r.page, r.sub, r.tab);
  else if (r.sub) navigateGlobal(r.page, r.sub);
  else navigateGlobal(r.page);
}

export interface RowLinkProps {
  onClick: (e: React.MouseEvent<any>) => void;
  onAuxClick: (e: React.MouseEvent<any>) => void;
  onMouseDown: (e: React.MouseEvent<any>) => void;
}

/**
 * Props für klickbare Nicht-Anker-Elemente. `url` darf null sein (dann reiner
 * Klick-Handler ohne Neu-Tab-Verhalten). `onOpen` ist die normale Aktion
 * (Drawer öffnen, navigate, ...).
 */
export function rowLinkProps(url: string | null | undefined, onOpen: (e: React.MouseEvent<any>) => void): RowLinkProps {
  return {
    onClick: (e) => {
      if (url && isNewTabClick(e)) { e.preventDefault(); e.stopPropagation(); openInNewTab(url); return; }
      onOpen(e);
    },
    onAuxClick: (e) => {
      if (url && e.button === 1) { e.preventDefault(); e.stopPropagation(); openInNewTab(url); }
    },
    // verhindert den Autoscroll-Cursor beim Mittelklick
    onMouseDown: (e) => { if (e.button === 1) e.preventDefault(); },
  };
}

type AppLinkTarget =
  | { to: string }
  | { page: PageId; sub?: string | null; tab?: string | null; extra?: string | null };

export type AppLinkProps = AppLinkTarget
  & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
  & { stopPropagation?: boolean };

/** Echter Link für SPA-Routen. Normaler Klick = gleicher Tab, Modifier = Browser. */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(props, ref) {
  const { onClick, onAuxClick, onMouseDown, stopPropagation, children, ...rest } = props as any;
  let href: string;
  if ('to' in props) {
    href = props.to;
    delete (rest as any).to;
  } else {
    href = buildPath(props.page, props.sub, props.tab, props.extra);
    delete (rest as any).page; delete (rest as any).sub; delete (rest as any).tab; delete (rest as any).extra;
  }
  return (
    <a
      {...rest}
      ref={ref}
      href={href}
      // Mittelklick auf einen Link in einer klickbaren Zeile darf die Zeilen-URL nicht öffnen.
      onAuxClick={(e) => { if (stopPropagation) e.stopPropagation(); onAuxClick?.(e); }}
      onMouseDown={(e) => { if (stopPropagation) e.stopPropagation(); onMouseDown?.(e); }}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onClick?.(e);
        if (e.defaultPrevented) return;
        // Modifier / Mittelklick / target=_blank: Browser entscheiden lassen.
        if (isNewTabClick(e) || e.button !== 0 || rest.target === '_blank') return;
        e.preventDefault();
        navigateToPath(href);
      }}
    >
      {children}
    </a>
  );
});
